/** Turrets on ships and asteroids, and cannonballs. */
import { BOW_GUN, CANNON, CHARACTER, HARPOON, TURRET } from "../constants";
import { boxesOverlap, clamp, v2, vAdd, vDist, vNorm, vRotate, vScale, vSub, type Vec2 } from "../math";
import { T, bowGunFacing, isTurretSeat, pieceAnchor, pieceDef, pieceToGrid, tileAt, turnAt, type Grid } from "../tiles";
import { type CreatureState, type PlayerState, type ProjectileState, type SunDef, type TurretState } from "./types";
import { type ShipBody, type AsteroidBody, type Sim, asteroidBox, harpoonMount, shipToWorld, worldToShip } from "./ships";
import { upVector, characterBox, grappleHit, tetherPoint } from "./character";

/** What a turret stands on: a ship, or an asteroid (which never moves or turns). */
export type TurretHost = ShipBody | AsteroidBody;

export const isShip = (h: TurretHost): h is ShipBody => "state" in h;

/** A turret host's id: the ship's, or the asteroid's (whose cannonballs they are). */
export const hostId = (h: TurretHost): string => (isShip(h) ? h.state.id : h.def.id);

export function hostToWorld(h: TurretHost, local: Vec2): Vec2 {
  return isShip(h) ? shipToWorld(h, local) : v2(h.def.ox + local.x, h.def.oy + local.y);
}

export function worldToHost(h: TurretHost, world: Vec2): Vec2 {
  return isShip(h) ? worldToShip(h, world) : v2(world.x - h.def.ox, world.y - h.def.oy);
}

/** Its turrets' states, by anchor index (an asteroid's are made the first time they're needed). */
export function hostTurrets(h: TurretHost): Record<string, TurretState> {
  return isShip(h) ? h.state.turrets : (h.turrets ??= {});
}

/** What the turret a player is manning stands on: their ship, or an asteroid. */
export function turretHostOf(p: PlayerState, sim: Sim): TurretHost | undefined {
  if (p.frame) return sim.ships.get(p.frame);
  return p.gunningAsteroid ? sim.asteroids.find((a) => a.def.id === p.gunningAsteroid) : undefined;
}

/** A turret's state, made the first time it's needed. */
export function turretOf(host: TurretHost, anchor: number): TurretState {
  return (hostTurrets(host)[anchor] ??= { aim: Math.PI / 2, cooldown: 0, gunner: null });
}

/** Quarter turns the turret anchored at `anchor` is turned by in its host's grid (see turnAt): 0 on a ship. */
export const turretTurn = (grid: Grid, anchor: number): number => turnAt(grid, anchor % grid.w, Math.floor(anchor / grid.w));

/** Where a turret's gun pivots, in its host's grid: the middle of its housing. */
export function turretPivot(grid: Grid, anchor: number): Vec2 {
  // Every family's turret is the same size.
  const { w, h } = pieceDef(T.IRONCLAD_TURRET);
  return pieceToGrid(anchor % grid.w, Math.floor(anchor / grid.w), turretTurn(grid, anchor), w / 2, h * 0.8);
}

/**
 * The aim (see TurretState.aim) of a turret's gun pointed at world point
 * `at`, kept to its arc across the top: aiming below it swings to whichever
 * end is nearer. The aim is in the turret's own frame (turned with it, see
 * turretTurn), radians from its own +X.
 */
export function turretAimAt(host: TurretHost, anchor: number, at: Vec2): number {
  const pivot = turretPivot(host.grid, anchor);
  const local = worldToHost(host, at);
  const a = Math.atan2(local.y - pivot.y, local.x - pivot.x) - (turretTurn(host.grid, anchor) * Math.PI) / 2;
  const n = Math.atan2(Math.sin(a), Math.cos(a));
  if (n < 0) return n < -Math.PI / 2 ? TURRET.maxAim : TURRET.minAim;
  return clamp(n, TURRET.minAim, TURRET.maxAim);
}

/** The turret on `host` whose seat a player's head or middle is in, as its anchor index, or null. */
export function turretAround(p: PlayerState, host: TurretHost): number | null {
  // Aboard a ship the player's position is already in its grid; on an asteroid it's in the world.
  const U = upVector(p.up);
  for (const h of [CHARACTER.height / 2, CHARACTER.height - 0.4]) {
    const at = vAdd(p.position, vScale(U, h));
    const local = isShip(host) ? at : worldToHost(host, at);
    const x = Math.floor(local.x);
    const y = Math.floor(local.y);
    if (isTurretSeat(tileAt(host.grid, x, y))) return pieceAnchor(host.grid, y * host.grid.w + x);
  }
  return null;
}

/** The turret seat a player could climb into here (on their ship, or an asteroid they're on), or null. */
export function seatHere(p: PlayerState, sim: Sim): { host: TurretHost; anchor: number } | null {
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  if (ship) {
    const anchor = turretAround(p, ship);
    return anchor === null ? null : { host: ship, anchor };
  }
  if (p.frame) return null;
  for (const a of sim.asteroids) {
    if (!boxesOverlap(characterBox(p.position, p.up), asteroidBox(a.def))) continue;
    const anchor = turretAround(p, a);
    if (anchor !== null) return { host: a, anchor };
  }
  return null;
}

/** Where a turret's (or harpoon's) gun points: the tip of its barrel, and the way along it, in the world. */
export function turretMuzzle(host: TurretHost, anchor: number): { muzzle: Vec2; dir: Vec2 } {
  // The aim is in the turret's own frame: into the host's.
  const inHost = turretOf(host, anchor).aim + (turretTurn(host.grid, anchor) * Math.PI) / 2;
  const local = v2(Math.cos(inHost), Math.sin(inHost));
  return { muzzle: hostToWorld(host, vAdd(turretPivot(host.grid, anchor), vScale(local, TURRET.barrel))), dir: vRotate(local, isShip(host) ? host.state.angle : 0) };
}

/** Fire a turret's gun, if it has reloaded: a cannonball from the end of the barrel, the way it points. */
export function fireTurret(host: TurretHost, anchor: number, makeId: () => string): ProjectileState | null {
  const turret = turretOf(host, anchor);
  if (turret.cooldown > 0) return null;
  turret.cooldown = TURRET.cooldown;
  const { muzzle, dir } = turretMuzzle(host, anchor);
  const base = isShip(host) ? host.state.velocity : v2();
  return { id: makeId(), ownerShipId: hostId(host), position: muzzle, velocity: vAdd(base, vScale(dir, CANNON.speed)), life: CANNON.life };
}

/** Kinds of creature a harpoon holds on its line; the others it only hurts. */
const HOLDS: ReadonlySet<CreatureState["kind"]> = new Set(["whale", "gasbag", "eel", "carrier"]);

/**
 * Fire a ship's harpoon (see HARPOON), if it's reloaded and the ship's line
 * isn't out already: straight along the barrel out to HARPOON.range, into the
 * first thing there. A creature (`creatureAt` finds it): a whale or gas bag
 * holds the line, the others are only hit. Rock, or another ship: the line's
 * stuck in it. The ship's then on the line, as long as it is now. What it hit
 * (where, and the creature, if one), or null if it missed (or couldn't fire).
 */
export function fireHarpoon(ship: ShipBody, anchor: number, sim: Sim, creatureAt: (p: Vec2) => CreatureState | undefined): { at: Vec2; creature?: CreatureState } | null {
  const turret = turretOf(ship, anchor);
  if (turret.cooldown > 0 || ship.state.tether) return null;
  turret.cooldown = HARPOON.cooldown;
  const { muzzle, dir } = turretMuzzle(ship, anchor);
  const mount = harpoonMount(ship, anchor);
  const hit = grappleHit(sim, muzzle, vAdd(muzzle, dir), HARPOON.range, true, ship.state.id);
  const stuck = hit && tetherPoint({ anchor, point: hit.at, rock: hit.rock, ship: hit.ship, length: 0 }, sim);
  const reach = stuck ? vDist(muzzle, stuck) : HARPOON.range;
  // A creature first, if one's nearer.
  for (let d = 0.5; d < reach; d += 0.5) {
    const at = vAdd(muzzle, vScale(dir, d));
    const creature = creatureAt(at);
    if (!creature) continue;
    if (HOLDS.has(creature.kind)) ship.state.tether = { anchor, point: creature.position, creature: creature.id, length: Math.max(HARPOON.minLength, vDist(mount, creature.position)) };
    return { at, creature };
  }
  if (!hit || !stuck) return null;
  ship.state.tether = { anchor, point: hit.at, ...(hit.rock ? { rock: hit.rock } : {}), ...(hit.ship ? { ship: hit.ship } : {}), length: Math.max(HARPOON.minLength, vDist(mount, stuck)) };
  return { at: stuck };
}

/** Holding fire on a harpoon whose line is out reels it in (see HARPOON.reel). */
export function reelHarpoon(ship: ShipBody, dt: number): void {
  const t = ship.state.tether;
  if (t) t.length = Math.max(HARPOON.minLength, t.length - HARPOON.reel * dt);
}

/**
 * The pilot fires every bow gun on the ship (if they've reloaded): each
 * shoots straight along the ship's own X, the way it faces (see bowGunFacing).
 */
export function fireBowGuns(ship: ShipBody, makeId: () => string): ProjectileState[] {
  const s = ship.state;
  if (!ship.stats.bowGuns.length || (s.gunCooldown ?? 0) > 0) return [];
  s.gunCooldown = BOW_GUN.cooldown;
  return ship.stats.bowGuns.map((i) => {
    const facing = bowGunFacing(ship.grid.tiles[i]!);
    const muzzle = shipToWorld(ship, v2((i % ship.grid.w) + 0.5 + facing * 0.9, Math.floor(i / ship.grid.w) + 0.5));
    const dir = vRotate(v2(facing, 0), s.angle);
    return { id: makeId(), ownerShipId: s.id, position: muzzle, velocity: vAdd(s.velocity, vScale(dir, CANNON.speed)), life: CANNON.life };
  });
}

/** Asteroid turrets reload (ships' do in applyShipControl). */
export function coolAsteroidTurrets(sim: Sim, dt: number): void {
  for (const a of sim.asteroids) for (const t of Object.values(a.turrets ?? {})) t.cooldown = Math.max(0, t.cooldown - dt);
}

export function stepProjectile(p: ProjectileState, suns: SunDef[], dt: number): void {
  // Tiny pull toward the nearest sun so shots arc a little.
  let nearest: SunDef | undefined;
  let best = Infinity;
  for (const s of suns) {
    const d = vDist(s.position, p.position);
    if (d < best) {
      best = d;
      nearest = s;
    }
  }
  if (nearest) {
    const g = vScale(vNorm(vSub(nearest.position, p.position)), CANNON.gravity * dt);
    p.velocity = vAdd(p.velocity, g);
  }
  p.position = vAdd(p.position, vScale(p.velocity, dt));
  p.life -= dt;
}
